<script lang="ts" setup>
import { ROUTE_NAMES } from '@/router/conf';
import { isMcpServerEnabled } from '@/composables/useFeatureFlag';
import { useRoute } from 'vue-router';
import PersonalInfo from '@/components/settings/PersonalInfo.vue';
import { useI18n } from 'vue-i18n';
import useAuthenticatedQuery from '@/composables/apiServices/useAuthenticatedQuery';
import type {
  ISettingsSidebarEmits,
  ISettingsSidebarProps,
  ISettingsSidebarRoutes
} from '@/types/components/sidebar/SettingsSidebar';
import useSettingsService from '@/composables/apiServices/useSettingsService';

const props = defineProps<ISettingsSidebarProps>();
const emit = defineEmits<ISettingsSidebarEmits>();

const { t } = useI18n();
const route = useRoute();

const { getUpgrade } = useSettingsService();

const { userIndex } = useAuthenticatedQuery();

const { suspense: upgradeSuspense } = getUpgrade();

const isView = (routeName: string) => route.matched.some((route) => route.name === routeName);

const routes: ISettingsSidebarRoutes[] = [
  {
    testId: 'settings-general',
    label: 'settings.general.label',
    routeName: ROUTE_NAMES.SETTINGS_GENERAL
  },
  {
    testId: 'settings-account',
    label: 'settings.account.label',
    routeName: ROUTE_NAMES.SETTINGS_ACCOUNT
  },
  {
    testId: 'settings-appearance',
    label: 'settings.appearance.label',
    routeName: ROUTE_NAMES.SETTINGS_APPEARANCE
  },
  {
    testId: 'settings-security',
    label: 'settings.security.label',
    routeName: ROUTE_NAMES.SETTINGS_SECURITY
  },
  {
    testId: 'settings-filters',
    label: 'settings.filters.label',
    routeName: ROUTE_NAMES.SETTINGS_FILTERS
  },
  {
    testId: 'settings-signatures',
    label: 'settings.signatures.label',
    routeName: ROUTE_NAMES.SETTINGS_SIGNATURE,
    alternativeName: ROUTE_NAMES.SETTINGS_SIGNATURE_EDIT
  },
  {
    testId: 'settings-autoreply',
    label: 'settings.autoreply.label',
    routeName: ROUTE_NAMES.SETTINGS_AUTOREPLY
  },
  {
    testId: 'settings-folders',
    label: 'settings.folders.label',
    routeName: ROUTE_NAMES.SETTINGS_FOLDERS
  },
  {
    testId: 'settings-contacts',
    label: 'settings.contacts.label',
    routeName: ROUTE_NAMES.SETTINGS_CONTACTS
  }
];
if (isMcpServerEnabled) {
  routes.push({
    testId: 'settings-mcp-server',
    label: 'settings.mcp.label',
    routeName: ROUTE_NAMES.SETTINGS_MCP_SERVER
  });
}

const { data: upgradeData } = await upgradeSuspense();
if (upgradeData?.length) {
  routes.push({
    testId: 'settings-upgrade',
    label: 'settings.upgrade.label',
    routeName: ROUTE_NAMES.SETTINGS_UPGRADE,
    textClass: 'upgrade-text'
  });
}
const emitCloseSidebar = () => {
  emit('closeSidebar');
};
</script>

<template>
  <personal-info v-if="props.isSmallDisplay" :is-small-display="props.isSmallDisplay" />

  <VList
    data-onboarding="settings-sidebar"
    :class="{
      'px-2': props.isSmallDisplay,
      'pt-4': props.isSmallDisplay,
      'pb-8': props.isSmallDisplay
    }"
    :lines="false"
    class="settings"
    nav
  >
    <VListItem
      v-for="item in routes"
      :key="item.testId"
      :active="isView(item.routeName) || (!!item.alternativeName && isView(item.alternativeName))"
      :class="{
        'px-4': !props.isSmallDisplay,
        'py-4': props.isSmallDisplay,
        'px-2': props.isSmallDisplay
      }"
      :data-test-id="item.testId"
      :to="{ name: item.routeName, params: { userIndex } }"
      @click="emitCloseSidebar"
      class="ma-0"
    >
      <VListItemTitle sentry-unmask :class="item.textClass">
        {{ t(item.label) }}
      </VListItemTitle>

      <template v-if="props.isSmallDisplay" #append>
        <VIcon sentry-unmask color="on-surface">mdi-chevron-right</VIcon>
      </template>
    </VListItem>
  </VList>
</template>

<style lang="scss" scoped>
@use '../../assets/styles/settings' as settings;
@use 'sass:map';

.settings {
  &--with-overlay {
    z-index: 1009;
  }

  .v-list-item {
    padding-top: 0.175rem;
    padding-bottom: 0.175rem;
    color: rgb(var(--v-theme-on-surface-variant));

    :deep(.v-list-item__overlay) {
      background-color: transparent;
    }

    &:hover {
      :deep(.v-list-item__overlay) {
        background-color: currentcolor;
      }
    }

    &--nav {
      @media screen and #{map.get(settings.$display-breakpoints, 'sm-and-up')} {
        border-bottom-left-radius: 0;
        border-top-left-radius: 0;
      }
    }

    &-title {
      font-size: 1rem;
      line-height: 1.5rem;

      @media screen and #{map.get(settings.$display-breakpoints, 'sm-and-up')} {
        font-size: 0.875rem;
        line-height: 1.25rem;
      }
    }

    &--active {
      background-color: rgb(var(--v-theme-secondary-container));
      color: rgb(var(--v-theme-on-secondary-container));
    }

    .v-icon {
      opacity: unset;
    }
  }
}

.upgrade-text {
  font-size: 1rem !important;
  color: rgb(var(--v-theme-primary));
}
</style>
